import { fetchExcelData, getMedicalRefundList, uploadExel } from '@/api';
import LoadingView from '@/components/common/LoadingView';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  IExcelBody,
  IFormattedExcel,
  IMedicalRefund,
  IUploadExcelImagePayload,
} from '@/types/common.type';
import { getBillSliderSetting } from '@repo/constants';
import { MEDICAL_AIRPORT_ITEM } from '@repo/types';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import Slider from 'react-slick';
import styled, { css } from 'styled-components';
import { ICON_MAXIMIZE, ICON_MINIMIZE, ICON_CLOSE_WHITE } from '@repo/assets';
import ExcelTable from '@/components/MedicalReceipt/ExcelTable';
import { AxiosError } from 'axios';
import { convertComponentToImage, dataURItoBlob } from '@repo/utils';
import { ICON_REFRESH_GRAY } from '@repo/assets';
import { Icon } from '@successmode-ktp/kui';

type Props = {
  billList: MEDICAL_AIRPORT_ITEM[];
  openedId: number;
  onClose: () => void;
};

const DetailBillModal = ({ billList, openedId, onClose }: Props) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const receiptImageRef = useRef<HTMLImageElement>(null);
  const excelRef = useRef<HTMLDivElement | null>(null);
  const ModalRef = useRef(null);
  const { passportNumber } = useAppSelector((state) => state) as IGlobalState;
  const [isLoading, setIsLoading] = useState(true);
  const [openedReceipt, setOpenedReceipt] = useState<string | null>(null);
  const [isVerticalScreen, setIsVerticalScreen] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [receiptList, setReceiptList] = useState([]);
  const [showIndex, setShowIndex] = useState(
    billList.length > 0
      ? billList.findIndex((d) => d.refundIndex === openedId)
      : 0,
  );
  const [nowPage, setNowPage] = useState(0);

  const { data } = useQuery(
    ['AirportReceiptList'],
    () => (!!passportNumber ? getMedicalRefundList(passportNumber) : null),
    {
      onSuccess: (list) => {
        const sortedList = billList.map((bill) =>
          list?.find((d) => d.refundIndex === bill.refundIndex),
        );
        setReceiptList(sortedList as any);
      },
      enabled: !!passportNumber,
    },
  );

  const handleCloseImageContainer = (e: any) => {
    if (e.target !== receiptImageRef.current) {
      setOpenedReceipt(null);
    }
  };

  useEffect(() => {
    const updateDeviceRotate = () => {
      const { innerWidth: width, innerHeight: height } = window;
      const isVerticalScreen = width <= height;
      setIsVerticalScreen(isVerticalScreen);
    };

    updateDeviceRotate();
    window.addEventListener('resize', updateDeviceRotate);

    return () => {
      window.removeEventListener('resize', updateDeviceRotate);
    };
  }, []);

  useEffect(() => {
    if (!!openedReceipt && imageContainerRef.current) {
      imageContainerRef.current.scrollLeft =
        imageContainerRef.current.scrollWidth;
    }
  }, [openedReceipt]);

  const { data: excelData, mutate: fetchMutate } = useMutation<
    IFormattedExcel,
    AxiosError,
    IExcelBody
  >('excelData', (payload) => fetchExcelData(payload), {
    retry: false,
    onError: () => {
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
      setIsLoading(false);
    },
    onSuccess: () => {
      setTimeout(uploadExcelImage, 500);
    },
  });

  const uploadExcelImage = async () => {
    if (excelRef) {
      const dataUrl = await convertComponentToImage(
        excelRef.current as HTMLElement,
      );
      const formData = new FormData();
      formData.append('file', dataURItoBlob(dataUrl));
      formData.append('refundId', openedId.toString());
      uploadMutate({ formData });
    }
  };

  const { mutate: uploadMutate } = useMutation<
    string,
    AxiosError,
    IUploadExcelImagePayload
  >((payload) => uploadExel(payload), {
    retry: false,
    onError: () => {
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
      setIsLoading(false);
    },
    onSuccess: (s3Url) => {
      setImageUrl(s3Url);
      setIsLoading(false);
    },
  });

  const makeReceiptFile = (refundIndex: number) => {
    setIsLoading(true);
    fetchMutate({
      touristIndex:
        billList.find((item) => item.refundIndex === openedId)?.touristIndex ||
        0,
      refundIndex,
    });
  };

  const setting =
    data &&
    getBillSliderSetting({
      length: billList.length,
      showIndex: showIndex,
      setCurrentSlide: (current) => setShowIndex(current),
    });

  if (data) {
    return (
      <>
        <Modal isOpen={openedId > -1} ref={ModalRef}>
          {receiptList && (
            <StyledSlider
              {...setting}
              afterChange={(v) => {
                setNowPage(v);
              }}
              nextArrow={
                <NextButton $disabled={billList.length === nowPage + 1}>
                  <Icon.ChevronRight
                    size={24}
                    fillColor={
                      billList.length === nowPage + 1 ? '#999' : '#111'
                    }
                  />
                </NextButton>
              }
              prevArrow={
                <PrevButton $disabled={nowPage === 0}>
                  <Icon.ChevronLeft
                    size={24}
                    fillColor={nowPage === 0 ? '#999' : '#111'}
                  />
                </PrevButton>
              }
            >
              {receiptList.map((item: IMedicalRefund) => {
                const info = {
                  hospitalName: item.hospitalName,
                  refundIndex: item.refundIndex,
                  supplyDate: item.supplyDate || '',
                  price: item.price || 0,
                  refund: item.refund || 0,
                  receiptPath: item.receiptPath || imageUrl,
                };
                return (
                  <ReceiptSection key={item.refundIndex}>
                    <ReceiptModalImage
                      src={info.receiptPath}
                      onLoad={() => setIsLoading(false)}
                    />
                    <SizeButton
                      onClick={() => setOpenedReceipt(info.receiptPath)}
                    >
                      <img src={ICON_MAXIMIZE} alt='' width={32} />
                    </SizeButton>
                    {isLoading && <LoadingView />}
                  </ReceiptSection>
                );
              })}
            </StyledSlider>
          )}
          <CloseButton
            onClick={() => {
              setShowIndex(0);
              onClose();
            }}
          >
            <img src={ICON_CLOSE_WHITE} alt='' width={24} />
          </CloseButton>
          <RefreshButton
            onClick={() =>
              billList[data.length - showIndex - 1] &&
              makeReceiptFile(billList[data.length - showIndex - 1].refundIndex)
            }
          >
            <img src={ICON_REFRESH_GRAY} alt='' width={20} />
            Refresh image
          </RefreshButton>
        </Modal>
        {openedId > -1 && <Backdrop />}
        {openedReceipt && (
          <ImageContainer
            ref={imageContainerRef}
            onClick={handleCloseImageContainer}
            isVerticalScreen={isVerticalScreen}
          >
            <MaxReceiptModalImage
              src={openedReceipt}
              isVerticalScreen={isVerticalScreen}
            />
            <CloseButtonWrapper
              isVerticalScreen={isVerticalScreen}
              ref={receiptImageRef}
            >
              <button>
                <img src={ICON_MINIMIZE} alt='' width={32} />
              </button>
            </CloseButtonWrapper>
          </ImageContainer>
        )}
        {excelData && (
          <Hidden>
            <ExcelContainer ref={excelRef}>
              <ExcelTable data={excelData} />
            </ExcelContainer>
          </Hidden>
        )}
      </>
    );
  }

  return null;
};

const Hidden = styled.div`
  position: absolute;
  top: -4000px;
  left: -2000px;
  width: 1200px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
const ExcelContainer = styled.div<{ ref: any }>`
  width: 1200px;
  position: absolute;
  top: -2500px;
  left: 400px;
`;

const RefreshButton = styled.button`
  position: fixed;
  bottom: 16%;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 4px;
  background: #f4f4f4;
  color: #333;
  font-size: 13px;
  font-weight: 500;
`;

const Modal = styled.aside<{ isOpen: boolean }>`
  position: fixed;
  top: ${({ isOpen }) => (isOpen ? '50%' : '100%')};
  left: 50%;
  transform: ${({ isOpen }) =>
    isOpen ? 'translate(-50%, -50%)' : 'translateX(-50%)'};
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: transparent;
  z-index: 200;
  transition: top 400ms;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  &::-webkit-scrollbar {
    display: none;
  }

  .slick-slider {
    margin-top: calc(50dvh - 469px / 2) !important;
  }
`;

const Backdrop = styled.aside`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  opacity: 0.8;
  z-index: 100;
  overflow: hidden;
`;

const StyledSlider = styled(Slider)`
  width: 335px;
  height: auto;
  overflow-y: auto;
  margin-top: 25vh;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ReceiptModalImage = styled.img`
  max-width: 335px;
  max-height: 80%;
`;

const ReceiptSection = styled.section`
  position: relative;
`;

const SizeButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
`;
const MaxReceiptModalImage = styled.img<{ isVerticalScreen: boolean }>`
  position: absolute;
  z-index: 1000;
  max-width: none;
  max-height: none;
  width: ${({ isVerticalScreen }) => (isVerticalScreen ? '90dvh' : '80vw')};

  padding-top: 40px;
  ${({ isVerticalScreen }) =>
    !isVerticalScreen &&
    css`
      padding-bottom: 40px;
    `}

  top: ${({ isVerticalScreen }) => (isVerticalScreen ? '50%' : '0')};
  left: ${({ isVerticalScreen }) => (isVerticalScreen ? '50vw' : '50%')};
  transform: ${({ isVerticalScreen }) =>
    isVerticalScreen ? 'translateY(-50%) rotate(90deg)' : 'translateX(-50%)'};
`;

const ImageContainer = styled.div<{ isVerticalScreen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100dvh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: ${({ isVerticalScreen }) =>
    isVerticalScreen ? 'auto hidden' : 'hidden auto'};
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5);
`;

const CloseButtonWrapper = styled.div<{ isVerticalScreen: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  position: absolute;
  z-index: 1000;
  max-width: none;
  max-height: none;
  width: ${({ isVerticalScreen }) => (isVerticalScreen ? '90dvh' : '80vw')};
  aspect-ratio: 717 / 1004;

  padding-top: 40px;
  padding-right: 16px;
  ${({ isVerticalScreen }) =>
    !isVerticalScreen &&
    css`
      padding-top: 56px;
      padding-bottom: 40px;
    `}

  top: ${({ isVerticalScreen }) => (isVerticalScreen ? '50%' : '0')};
  left: ${({ isVerticalScreen }) => (isVerticalScreen ? '50vw' : '50%')};
  transform: ${({ isVerticalScreen }) =>
    isVerticalScreen ? 'translateY(-50%) rotate(90deg)' : 'translateX(-50%)'};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 40px;
  right: 20px;
  width: 24px;
  height: 24px;
`;

const ArrowButton = styled.button`
  position: fixed;
  top: 65%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 100px;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.15));
  background-color: #fff !important;
  z-index: 300;

  &::before {
    content: '';
  }

  &:hover {
    opacity: 1 !important;
  }
`;

const PrevButton = styled(ArrowButton)<{ $disabled: boolean }>`
  left: 10px;
  background-color: ${({ $disabled }) =>
    $disabled ? '#E5E6E8' : '#fff'} !important;
`;

const NextButton = styled(ArrowButton)<{ $disabled: boolean }>`
  right: 10px;
  background-color: ${({ $disabled }) =>
    $disabled ? '#E5E6E8' : '#fff'} !important;
`;

export default DetailBillModal;
